import frames from "frames";
import React, { Fragment } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { useApplicationContext } from "./ApplicationContext";
import { AuthLayout } from "./AuthLayout";
import { AuthRedirector } from "./AuthRedirector";
import { MainLayout } from "./MainLayout";
import { MainRedirector } from "./MainRedirector";

import "styles/theme.less";

export const Application: React.FC<{}> = () => {
  const applicationContext = useApplicationContext();
  const location = useLocation();
  const { i18n } = useTranslation();

  const helmet = (
    <Helmet>
      <html lang={i18n.language} />
      <meta charSet="utf-8" />
      <link rel="icon" type="image/png" href="/favicon.png" />
      <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no" />
      <title>Pgn Showroom</title>
      <body />
    </Helmet>
  );

  let application = <Fragment></Fragment>;

  if (!applicationContext.authenticated) {
    console.info("Not authenticated!");

    application = (
      <Routes>
        <Route path="/" element={<AuthRedirector />} />
        <Route element={<AuthLayout />}>
          <Route path="/auth/sign-in" element={<frames.AuthSignIn />} />
          <Route path="/auth/password-reset/:token" element={<frames.AuthCompletePasswordReset />} />
          <Route path="/auth/password-reset" element={<frames.AuthRequestPasswordReset />} />
        </Route>
        <Route path="*" element={<AuthRedirector />} />
      </Routes>
    );
  } else if (location.state && (location.state as any).landingUrl) {
    application = <Navigate to={(location.state as any).landingUrl} replace />;
  } else if (applicationContext.session.currentUser.role === "ADMIN") {
    application = (
      <Routes>
        <Route element={<MainLayout />}>
          <Route path="/" element={<MainRedirector />} />
          <Route path="/global">
            <Route path="/stats" element={<frames.StatsDash />} />
            <Route path="/users">
              <Route path="/new" element={<frames.AccountsUsersNew />} />
              <Route path="/:id/edit" element={<frames.AccountsUsersEdit />} />
              <Route path="/:id" element={<frames.AccountsUsersShow />} />
              <Route path="/" element={<frames.AccountsUsersList />} />
            </Route>
            <Route path="/user_brands">
              <Route path="/new/:userId" element={<frames.AccountsUserBrandsNew />} />
              <Route path="/new" element={<frames.AccountsUserBrandsNew />} />
              <Route path="/:id/edit/:userId" element={<frames.AccountsUserBrandsEdit />} />
              <Route path="/:id/edit" element={<frames.AccountsUserBrandsEdit />} />
              <Route path="/:id" element={<frames.AccountsUserBrandsShow />} />
              <Route path="/" element={<frames.AccountsUserBrandsList />} />
            </Route>
            <Route path="/orders">
              <Route path="/:id" element={<frames.OrdersDocumentsShow />} />
              <Route path="/" element={<frames.OrdersDocumentsList />} />
            </Route>
            <Route path="/carts">
              <Route path="/:id" element={<frames.OrdersCartsShow />} />
              <Route path="/" element={<frames.OrdersCartsList />} />
            </Route>
            <Route path="/items">
              <Route path="/new" element={<frames.ArticlesItemsNew />} />
              <Route path="/:id/edit" element={<frames.ArticlesItemsEdit />} />
              <Route path="/:id" element={<frames.ArticlesItemsShow />}>
                <Route path="/variants">
                  <Route path="/new" element={<frames.ArticlesItemsVariantsNew />} />
                  <Route path="/" element={<frames.ArticlesItemsVariantsList />} />
                </Route>
                <Route path="/prices">
                  <Route path="/new" element={<frames.ArticlesItemsPricesNew />} />
                  <Route path="/:priceId/edit" element={<frames.ArticlesItemsPricesEdit />} />
                  <Route path="/" element={<frames.ArticlesItemsPricesList />} />
                </Route>
                <Route path="/images">
                  <Route path="/edit" element={<frames.ArticlesItemsImagesEdit />} />
                  <Route path="/" element={<frames.ArticlesItemsImagesList />} />
                </Route>
                <Route path="/suggestions">
                  <Route path="/edit" element={<frames.ArticlesItemsSuggestionsEdit />} />
                  <Route path="/" element={<frames.ArticlesItemsSuggestionsList />} />
                </Route>
                <Route path="/stocks" element={<frames.ArticlesItemsStocksList />} />
                <Route path="/" element={<frames.ArticlesItemsDetailsShow />} />
              </Route>
              <Route path="/" element={<frames.ArticlesItemsList />} />
            </Route>
            <Route path="/stocks">
              <Route path="/" element={<frames.StocksList />} />
            </Route>
            <Route path="/brands">
              <Route path="/new" element={<frames.BrandsNew />} />
              <Route path="/:id/edit" element={<frames.BrandsEdit />} />
              <Route path="/" element={<frames.BrandsList />} />
            </Route>
            <Route path="/seasons">
              <Route path="/new" element={<frames.SeasonsNew />} />
              <Route path="/:id/edit" element={<frames.SeasonsEdit />} />
              <Route path="/:id" element={<frames.SeasonsShow />} />
              <Route path="/" element={<frames.SeasonsList />} />
            </Route>
            <Route path="/lines">
              <Route path="/new" element={<frames.LinesNew />} />
              <Route path="/:id/edit" element={<frames.LinesEdit />} />
              <Route path="/:id" element={<frames.LinesShow />} />
              <Route path="/" element={<frames.LinesList />} />
            </Route>
            <Route path="/attributes">
              <Route path="/" element={<frames.AttributesDash />} />
              <Route path="/size_groups">
                <Route path="/" element={<frames.SizeGroupsList />} />
                <Route path="/new" element={<frames.SizeGroupsNew />} />
                <Route path="/:id/edit" element={<frames.SizeGroupsEdit />} />
              </Route>
              <Route path="/sizes">
                <Route path="/" element={<frames.SizesList />} />
                <Route path="/new" element={<frames.SizesNew />} />
                <Route path="/:id/edit" element={<frames.SizesEdit />} />
              </Route>
              <Route path="/composition_groups">
                <Route path="/" element={<frames.CompositionGroupsList />} />
                <Route path="/new" element={<frames.CompositionGroupsNew />} />
                <Route path="/:id/edit" element={<frames.CompositionGroupsEdit />} />
              </Route>
              <Route path="/compositions">
                <Route path="/" element={<frames.CompositionsList />} />
                <Route path="/new" element={<frames.CompositionsNew />} />
                <Route path="/:id/edit" element={<frames.CompositionsEdit />} />
              </Route>
              <Route path="/color_groups">
                <Route path="/" element={<frames.ColorGroupsList />} />
                <Route path="/new" element={<frames.ColorGroupsNew />} />
                <Route path="/:id/edit" element={<frames.ColorGroupsEdit />} />
              </Route>
              <Route path="/colors">
                <Route path="/" element={<frames.ColorsList />} />
                <Route path="/new" element={<frames.ColorsNew />} />
                <Route path="/:id/edit" element={<frames.ColorsEdit />} />
              </Route>
              <Route path="/kind_groups">
                <Route path="/" element={<frames.KindGroupsList />} />
                <Route path="/new" element={<frames.KindGroupsNew />} />
                <Route path="/:id/edit" element={<frames.KindGroupsEdit />} />
              </Route>
              <Route path="/kinds">
                <Route path="/" element={<frames.KindsList />} />
                <Route path="/new" element={<frames.KindsNew />} />
                <Route path="/:id/edit" element={<frames.KindsEdit />} />
              </Route>
              <Route path="/fittings">
                <Route path="/" element={<frames.FittingsList />} />
                <Route path="/new" element={<frames.FittingsNew />} />
                <Route path="/:id/edit" element={<frames.FittingsEdit />} />
              </Route>
              <Route path="/price_lists">
                <Route path="/" element={<frames.PriceListsList />} />
                <Route path="/new" element={<frames.PriceListsNew />} />
                <Route path="/:id/edit" element={<frames.PriceListsEdit />} />
              </Route>
            </Route>
            <Route path="/sessions">
              <Route path="/" element={<frames.SessionsList />} />
            </Route>
          </Route>
        </Route>
        <Route path="/auth/*" element={<MainRedirector />} />
        <Route path="/global/*" element={<MainRedirector />} />
        <Route path="*" element={<MainRedirector />} />
      </Routes>
    );
  } else if (applicationContext.session.currentUser.role === "SALESPERSON") {
    application = (
      <Routes>
        <Route element={<MainLayout />}>
          <Route path="/" element={<MainRedirector />} />
          <Route path="/global">
            <Route path="/stats" element={<frames.StatsDash />} />
            <Route path="/customers">
              <Route path="/" element={<frames.AccountsCustomersList />} />
            </Route>
            <Route path="/orders">
              <Route path="/:id" element={<frames.OrdersDocumentsShow />} />
              <Route path="/" element={<frames.OrdersDocumentsList />} />
            </Route>
            <Route path="/carts">
              <Route path="/:id" element={<frames.OrdersCartsShow />} />
              <Route path="/" element={<frames.OrdersCartsList />} />
            </Route>
            <Route path="/sessions">
              <Route path="/" element={<frames.SessionsList />} />
            </Route>
          </Route>
        </Route>
        <Route path="/auth/*" element={<MainRedirector />} />
        <Route path="/global/*" element={<MainRedirector />} />
        <Route path="*" element={<MainRedirector />} />
      </Routes>
    );
  } else {
    window.location.assign(applicationContext.siteEnvironment.appEndpointPublic);
  }

  return (
    <Fragment>
      {helmet}
      {application}
    </Fragment>
  );
};
